import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  IntakePortalNotFoundError,
  fetchIntakePortalState,
  type IntakePortalState,
} from '../../lib/api.js';
import { IntakeShell } from './_components/IntakeShell.js';
import { renderIntakeStep } from './_components/renderIntakeStep.js';

/**
 * wd-2.3.1 — Prospect-facing intake portal page. Reads `:token` from
 * the URL, fetches the server's hydrated `TenantIntake`, and hands
 * off to `IntakeShell`. Unauthenticated — the token is the
 * authorization; the server's gate 404s every other case.
 *
 * This page lives under the admin-app bundle per DECISIONS-2026-04-21-PM.md §4
 * (nested under admin app at `/intake/:token`). A future reverse-proxy
 * rewrite will serve `intake.app.animalloversapp.com/{token}` from the
 * same admin-app bundle so prospects land on the branded subdomain.
 */

type IntakePortalProps = {
  config: ClientConfig;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; intake: IntakePortalState };

export function IntakePortal({ config }: IntakePortalProps): JSX.Element {
  const params = useParams<{ token?: string }>();
  const token = params.token ?? '';
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (token.trim() === '') {
      setState({ kind: 'not_found' });
      return;
    }
    const controller = new AbortController();
    const load = async (): Promise<void> => {
      try {
        const intake = await fetchIntakePortalState(
          config.deployment.apiUrl,
          token,
          controller.signal,
        );
        setState({ kind: 'ready', intake });
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof IntakePortalNotFoundError) {
          setState({ kind: 'not_found' });
          return;
        }
        const message =
          err instanceof Error ? err.message : 'Failed to load intake.';
        setState({ kind: 'error', message });
      }
    };
    setState({ kind: 'loading' });
    void load();
    return () => {
      controller.abort();
    };
  }, [config.deployment.apiUrl, token]);

  if (state.kind === 'loading') {
    return (
      <main className="ferro-intake ferro-intake--status" aria-busy="true">
        <section className="ferro-intake__statusCard">
          <h1 className="ferro-intake__statusTitle">Loading your intake…</h1>
          <p className="ferro-muted">This takes just a moment.</p>
        </section>
      </main>
    );
  }

  if (state.kind === 'not_found') {
    return (
      <main className="ferro-intake ferro-intake--status">
        <section className="ferro-intake__statusCard">
          <h1 className="ferro-intake__statusTitle">
            This intake link is no longer valid
          </h1>
          <p className="ferro-muted">
            Your invite may have expired or already been submitted. Reply
            to your invite email and we'll send you a fresh link.
          </p>
        </section>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main className="ferro-intake ferro-intake--status">
        <section className="ferro-intake__statusCard">
          <h1 className="ferro-intake__statusTitle">
            We couldn't load your intake
          </h1>
          <p className="ferro-muted">{state.message}</p>
          <p className="ferro-muted">
            Refresh this page to try again. If the problem persists, reply
            to your invite email.
          </p>
        </section>
      </main>
    );
  }

  return (
    <IntakeShell
      apiUrl={config.deployment.apiUrl}
      token={token}
      initialIntake={state.intake}
      renderStep={renderIntakeStep}
    />
  );
}
